import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Phone, ShieldCheck, Star, MapPin, Sparkles, Home as HomeIcon, Snowflake, Building2, Layers, Wrench, Wind, Award, Euro, Camera, Sprout, Flame } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { COMPANY, SEASONAL_OFFER, SERVICES, TRUST_ITEMS, GALLERY_ITEMS, GALLERY_CATEGORIES } from "@/config/site";
import Placeholder from "@/components/Placeholder";
import Reveal from "@/components/Reveal";
import useSEO, { SITE_URL } from "@/hooks/useSEO";

const serviceIcon = (slug) => {
  const map = {
    "renovation-generale": "home",
    climatisation: "snowflake",
    facade: "building",
    toiture: "layers",
    isolation: "layers",
    peinture: "palette",
    plomberie: "droplets",
    electricite: "zap",
  };
  return map[slug] || "wrench";
};

const serviceLucide = (slug) => {
  const map = {
    "renovation-generale": HomeIcon,
    climatisation: Snowflake,
    "poele-granules": Flame,
    facade: Building2,
    toiture: Layers,
    surveillance: Camera,
    arrosage: Sprout,
  };
  return map[slug] || Wrench;
};

// Hero visuals — real images
const HERO_IMAGE_MAIN = GALLERY_ITEMS.find((i) => i.id === "p-clim-01")?.image;
const HERO_IMAGE_ACCENT = GALLERY_ITEMS.find((i) => i.id === "p-clim-02")?.image;

export default function Home() {
  const previewServices = SERVICES.slice(0, 4);
  const previewGallery = GALLERY_ITEMS.slice(0, 5);

  useSEO({
    title: "Rénovation, travaux & climatisation à Tours, Fondettes (37)",
    description:
      "PENOW, plateforme locale de mise en relation à Tours, Fondettes et en Indre-et-Loire (37). Nous vous connectons avec des professionnels partenaires qualifiés pour vos travaux : rénovation, climatisation, PAC, poêle à granulés, façade, toiture, isolation, vidéosurveillance, arrosage automatique. Devis gratuit sous 48h. Nous ne réalisons pas les travaux nous-mêmes.",
    keywords:
      "travaux Tours, rénovation Tours, rénovation 37, travaux 37, mise en relation artisan Tours, apporteur d'affaires bâtiment 37, plateforme travaux Tours, climatisation Tours, climatisation Fondettes, pompe à chaleur 37, PAC air/air Indre-et-Loire, poêle à granulés Tours, installation poêle pellets Fondettes, chauffage bois 37, ravalement façade Tours, nettoyage toiture 37, isolation combles Tours, installation caméra de surveillance Tours, vidéosurveillance Fondettes, alarme maison 37, arrosage automatique Tours, arrosage enterré Fondettes, nettoyage extérieur 37, réseau artisans partenaires RGE QualiPAC Qualibois, plateforme travaux Centre-Val de Loire",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`,
          name: "PENOW",
          url: `${SITE_URL}/`,
          inLanguage: "fr-FR",
        },
        {
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Combien coûte l'installation d'une climatisation à Tours ou Fondettes ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Le prix d'une climatisation dépend du type (split, multi-split, gainable, PAC air/eau), de la surface à traiter et de la configuration du logement. Pour un split mural mono-zone posé par un professionnel partenaire, comptez à partir de 1 500 € TTC. Pour une PAC air/eau avec pose, entre 8 000 € et 15 000 € avant aides. Les partenaires PENOW vous transmettent un devis détaillé et gratuit sous 48h.",
              },
            },
            {
              "@type": "Question",
              name: "Suis-je éligible aux aides pour ma pompe à chaleur en 2025 ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Oui, la plupart des installations de PAC air/air ou air/eau ouvrent droit à MaPrimeRénov', aux CEE et à la TVA à 5,5%, à condition d'être posées par un professionnel certifié RGE QualiPAC. PENOW ne vous met en relation qu'avec des professionnels partenaires disposant de cette qualification. Selon vos revenus, la prime peut atteindre 5 000 €.",
              },
            },
            {
              "@type": "Question",
              name: "Combien de temps dure un chantier de rénovation ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Un ravalement de façade prend en général 1 à 3 semaines, un démoussage de toiture 1 à 2 jours, l'installation d'une PAC 1 à 2 jours, une rénovation complète d'appartement 4 à 12 semaines. Nous vous remettons un planning détaillé avant le début du chantier.",
              },
            },
            {
              "@type": "Question",
              name: "Travaillez-vous avec des artisans certifiés RGE ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Oui — PENOW s'appuie sur un réseau d'artisans certifiés RGE QualiPAC et RGE Qualibat. Cela vous permet de bénéficier des aides de l'État (MaPrimeRénov', CEE, TVA 5,5%) sur les prestations éligibles, et garantit un niveau de qualité conforme aux exigences des certifications.",
              },
            },
            {
              "@type": "Question",
              name: "Comment se déroule mon devis ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Après votre demande, nous convenons d'un rendez-vous sur site (gratuit et sans engagement). Nous étudions vos besoins, mesurons les contraintes techniques, puis vous transmettons un devis détaillé sous 48h. Aucun coût caché, planning et modalités clairs.",
              },
            },
            {
              "@type": "Question",
              name: "Intervenez-vous dans tout le département 37 ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Oui, nous intervenons dans toute l'Indre-et-Loire (Tours, Fondettes, Saint-Cyr-sur-Loire, Joué-lès-Tours, Amboise, Chinon, Loches…) et étendons nos prestations à la région Centre-Val de Loire pour les chantiers importants.",
              },
            },
          ],
        },
      ],
    },
  });

  return (
    <div data-testid={HOME.hero}>
      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden">
        <div className="container-editorial pt-14 md:pt-20 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left */}
          <div className="lg:col-span-7">
            <div className="eyebrow mb-6">Mise en relation à Tours (37)</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-anthracite">
              Votre projet,
              <br />
              <span className="text-terracotta">entre les meilleures</span> mains.
            </h1>
            <p className="mt-8 text-lg text-anthracite/70 max-w-xl leading-relaxed">
              <strong className="font-semibold text-anthracite">PENOW met en relation particuliers et professionnels avec des artisans qualifiés du bâtiment</strong> à Tours, Fondettes et en Indre-et-Loire (37).
              Rénovation, climatisation, pompe à chaleur, façade, toiture, isolation… un seul contact, le bon professionnel partenaire pour votre projet.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid={HOME.heroCta}
                className="inline-flex items-center gap-2 bg-terracotta hover:bg-copper text-white px-7 py-4 tracking-wide transition-colors"
              >
                Devis gratuit
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link
                to="/services"
                data-testid={HOME.heroSecondaryCta}
                className="inline-flex items-center gap-2 text-anthracite hover:text-terracotta transition-colors px-2"
              >
                Découvrir nos métiers
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>

            {/* Micro trust row */}
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 text-xs uppercase tracking-[0.22em] text-anthracite/50">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-terracotta" strokeWidth={1.5} /> Partenaires sélectionnés</span>
              <span className="flex items-center gap-2"><Star className="w-4 h-4 text-terracotta" strokeWidth={1.5} /> 4.9/5 clients</span>
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-terracotta" strokeWidth={1.5} /> Centre-Val de Loire</span>
            </div>
          </div>

          {/* Right — Asymmetric composition with real photos */}
          <div className="lg:col-span-5 relative">
            <div className="relative">
              <div className="relative border border-black/10 overflow-hidden project-photo-frame" style={{ aspectRatio: "4/5" }}>
                <img
                  src={HERO_IMAGE_MAIN}
                  alt="Réalisation d'un professionnel partenaire — climatisation gainable en pièce de vie"
                  decoding="async"
                  fetchPriority="high"
                  className="hero-photo absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-anthracite/85 backdrop-blur-sm text-white text-[10px] uppercase tracking-[0.28em] px-3 py-1.5">
                  Réalisation récente
                </div>
              </div>

              <div className="hidden md:block absolute -bottom-8 -left-8 w-40 border border-black/10 overflow-hidden aspect-square project-photo-frame">
                <img
                  src={HERO_IMAGE_ACCENT}
                  alt="Détail — climatisation split chambre"
                  loading="lazy"
                  decoding="async"
                  className="hero-photo w-full h-full object-cover"
                />
              </div>

              <div className="hidden md:flex absolute -top-6 -right-6 items-center gap-3 bg-white border border-black/10 px-4 py-3 shadow-sm">
                <div className="w-10 h-10 bg-terracotta text-white flex items-center justify-center">
                  <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-anthracite/50">Devis</div>
                  <div className="text-sm font-medium text-anthracite">Sous 48h — gratuit</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== TRUST BANNER ===================== */}
      <section className="bg-deep-blue text-white" data-testid={HOME.trustBanner}>
        <div className="container-editorial py-10 md:py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          {TRUST_ITEMS.map((t, i) => (
            <Reveal key={t.label} delay={i * 80}>
              <div className="border-l border-white/15 pl-5 md:pl-6">
                <div className="font-display text-3xl md:text-4xl text-white">{t.value}</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-white/50">{t.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===================== SEASONAL OFFER ===================== */}
      <section
        className="container-editorial py-20 md:py-28"
        data-testid={HOME.seasonalOffer}
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="eyebrow mb-4">Offre du moment · {SEASONAL_OFFER.season}</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-anthracite">
              {SEASONAL_OFFER.headline}
            </h2>
            <p className="mt-6 text-anthracite/60 leading-relaxed">
              {SEASONAL_OFFER.intro}
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 link-arrow text-terracotta text-sm"
            >
              Demander un diagnostic gratuit
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>

          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {SEASONAL_OFFER.items.map((item, i) => (
              <Reveal
                key={item.key}
                delay={i * 120}
                className={`hover-lift group relative border ${
                  i === 0 ? "border-terracotta bg-terracotta/[0.06]" : "border-black/10 bg-white"
                } p-6 md:p-7 flex flex-col`}
              >
                <div
                  className={`w-10 h-10 flex items-center justify-center border ${
                    i === 0 ? "border-terracotta text-terracotta" : "border-anthracite/20 text-anthracite/70"
                  }`}
                >
                  <span className="font-display text-lg">0{i + 1}</span>
                </div>
                <div className="mt-6 text-[10px] uppercase tracking-[0.28em] text-anthracite/50">
                  {item.subtitle}
                </div>
                <h3
                  data-testid={HOME.seasonalCard}
                  className="mt-2 font-display text-2xl text-anthracite"
                >
                  {item.title}
                </h3>
                <p className="mt-3 text-sm text-anthracite/60 leading-relaxed flex-1">
                  {item.description}
                </p>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center gap-2 text-sm text-anthracite group-hover:text-terracotta transition-colors"
                >
                  Demander un devis
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CLIMATISATION & PAC (SEO) ===================== */}
      <section
        className="border-y border-black/5 bg-white"
        aria-labelledby="climatisation-title"
      >
        <div className="container-editorial py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4 flex items-center gap-2">
              <Wind className="w-3.5 h-3.5" strokeWidth={2} />
              Spécialité PENOW
            </div>
            <h2
              id="climatisation-title"
              className="font-display text-4xl md:text-5xl leading-tight text-anthracite"
            >
              Climatisation & pompe à chaleur à <span className="text-terracotta">Fondettes</span> et en Indre-et-Loire.
            </h2>
            <p className="mt-6 text-anthracite/70 leading-relaxed">
              PENOW vous met en relation avec des professionnels partenaires spécialisés dans l'installation de{" "}
              <strong className="font-semibold">climatisations réversibles</strong>{" "}
              et de <strong className="font-semibold">pompes à chaleur (PAC)</strong>{" "}
              à Fondettes, Tours et dans toute l'Indre-et-Loire. Les installations sont
              dimensionnées sur mesure par les partenaires, silencieuses, économes en énergie
              et éligibles aux aides de l'État (MaPrimeRénov', CEE) grâce à leurs{" "}
              <strong className="font-semibold">certifications RGE QualiPAC</strong>.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-terracotta/10 text-terracotta shrink-0">
                  <Award className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-sm font-medium text-anthracite">Partenaires RGE QualiPAC</div>
                  <div className="text-xs text-anthracite/50">Certifiés et sélectionnés</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-terracotta/10 text-terracotta shrink-0">
                  <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-sm font-medium text-anthracite">Assurances partenaires</div>
                  <div className="text-xs text-anthracite/50">Portées par chaque professionnel</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-terracotta/10 text-terracotta shrink-0">
                  <Euro className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-sm font-medium text-anthracite">Économies jusqu'à 70%</div>
                  <div className="text-xs text-anthracite/50">vs. chauffage électrique classique</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-terracotta/10 text-terracotta shrink-0">
                  <Snowflake className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-sm font-medium text-anthracite">Chaud & froid réversible</div>
                  <div className="text-xs text-anthracite/50">Confort toute l'année</div>
                </div>
              </div>
            </div>

            <Link
              to="/contact"
              data-testid="home-clim-cta"
              className="mt-10 inline-flex items-center gap-2 bg-terracotta hover:bg-copper text-white px-6 py-4 tracking-wide transition-colors"
            >
              Demander un devis climatisation
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: "PAC air/air (climatisation réversible)",
                  desc: "Split, multi-split ou gainable — chauffage l'hiver, fraîcheur l'été, une seule solution silencieuse et performante.",
                  points: ["Split mural / console / cassette", "Multi-split jusqu'à 5 pièces", "Gainable invisible plafond"],
                },
                {
                  title: "PAC air/eau (chauffage central)",
                  desc: "Remplacez votre ancienne chaudière fioul ou gaz par une pompe à chaleur air/eau. Éligible MaPrimeRénov' jusqu'à 5 000€.",
                  points: ["Compatible radiateurs & plancher chauffant", "Production d'eau chaude sanitaire", "Économies immédiates"],
                },
                {
                  title: "Étude thermique personnalisée",
                  desc: "Chaque projet démarre par une visite technique et un dimensionnement précis pour choisir la solution la plus efficace et la plus économique.",
                  points: ["Visite technique gratuite", "Simulation d'économies", "Devis détaillé sous 48h"],
                },
                {
                  title: "Dépannage & mise en service",
                  desc: "Intervention rapide en cas de panne, remplacement de composants, reprise d'installation existante.",
                  points: ["Intervention sous 48h", "Toutes marques", "Devis transparent"],
                },
              ].map((c, i) => (
                <Reveal
                  key={c.title}
                  delay={i * 80}
                  className="border border-black/10 bg-sand p-6 hover:border-terracotta transition-colors"
                >
                  <h3 className="font-display text-xl text-anthracite leading-snug">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-sm text-anthracite/60 leading-relaxed">
                    {c.desc}
                  </p>
                  <ul className="mt-4 space-y-1.5">
                    {c.points.map((p) => (
                      <li key={p} className="text-xs text-anthracite/70 flex items-start gap-2">
                        <span className="w-1 h-1 bg-terracotta rounded-full mt-1.5 shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== SERVICES PREVIEW ===================== */}
      <section className="bg-sand border-y border-black/5" data-testid={HOME.servicesPreview}>
        <div className="container-editorial py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="eyebrow mb-4">Nos métiers</div>
              <h2 className="font-display text-4xl md:text-5xl text-anthracite max-w-2xl">
                Un réseau, tous les métiers.
              </h2>
            </div>
            <Link
              to="/services"
              className="link-arrow text-anthracite text-sm inline-flex items-center gap-2"
            >
              Voir tous les services
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {previewServices.map((s, i) => {
              const Icon = serviceLucide(s.slug);
              return (
                <Reveal
                  key={s.slug}
                  delay={i * 80}
                  className="group bg-white border border-black/10 hover:border-copper transition-colors hover-lift p-8 flex flex-col relative"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-terracotta/10 text-terracotta mb-6 group-hover:bg-terracotta group-hover:text-white transition-colors">
                    <Icon className="w-7 h-7" strokeWidth={1.25} />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-anthracite/50 mb-2">
                    {s.short}
                  </div>
                  <h3 data-testid={HOME.servicePreviewCard} className="font-display text-xl text-anthracite">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-anthracite/60 flex-1 leading-relaxed">{s.description}</p>
                  <Link
                    to="/contact"
                    className="mt-6 inline-flex items-center gap-2 text-sm text-anthracite group-hover:text-terracotta transition-colors"
                  >
                    Demander un devis
                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== GALLERY PREVIEW ===================== */}
      <section className="container-editorial py-20 md:py-28" data-testid={HOME.galleryPreview}>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="eyebrow mb-4">Réalisations</div>
            <h2 className="font-display text-4xl md:text-5xl text-anthracite max-w-2xl">
              Quelques réalisations récentes de nos partenaires.
            </h2>
          </div>
          <Link to="/realisations" className="link-arrow text-anthracite text-sm inline-flex items-center gap-2">
            Toute la galerie
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>

        {/* Bento grid — real photos */}
        <div className="grid grid-cols-6 grid-rows-2 gap-4 h-[520px] md:h-[600px]">
          <Link
            to="/realisations"
            data-testid={HOME.galleryPreviewCard}
            className="col-span-6 md:col-span-3 row-span-2 relative group overflow-hidden border border-black/10 project-photo-frame"
          >
            <img src={previewGallery[0].image} alt={previewGallery[0].title} loading="lazy" decoding="async" className="project-photo absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03]" />
            <div className="absolute inset-0 bg-gradient-to-t from-anthracite/80 via-anthracite/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/70">
                {GALLERY_CATEGORIES.find((c) => c.key === previewGallery[0].category)?.label}
              </div>
              <div className="font-display text-2xl md:text-3xl text-white mt-1 leading-tight">
                {previewGallery[0].title}
              </div>
            </div>
          </Link>

          {previewGallery.slice(1, 5).map((it, idx) => {
            // 4 tiles in a 3-column × 2-row area on the right
            // Layout: idx 0 -> col-span-2 (big), idx 1 -> col-span-1, idx 2 -> col-span-2, idx 3 -> col-span-1
            const spans = ["md:col-span-2", "md:col-span-1", "md:col-span-2", "md:col-span-1"];
            return (
              <Link
                key={it.id}
                to="/realisations"
                data-testid={HOME.galleryPreviewCard}
                className={`col-span-3 ${spans[idx]} row-span-1 relative group overflow-hidden border border-black/10 project-photo-frame`}
              >
                <img src={it.image} alt={it.title} loading="lazy" decoding="async" className="project-photo absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05]" />
                <div className="absolute inset-0 bg-anthracite/0 group-hover:bg-anthracite/20 transition-colors" />
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2">
                  <div className="bg-white/95 backdrop-blur-sm text-anthracite px-3 py-1.5 text-[11px] tracking-wide max-w-[80%] truncate">
                    {it.title}
                  </div>
                  <div className="w-8 h-8 bg-terracotta text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ===================== ZONES D'INTERVENTION (Local SEO) ===================== */}
      <section
        className="bg-sand border-t border-black/5"
        aria-labelledby="zones-title"
        data-testid="home-zones"
      >
        <div className="container-editorial py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="eyebrow mb-4 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" strokeWidth={2} />
                Zones d'intervention
              </div>
              <h2
                id="zones-title"
                className="font-display text-4xl md:text-5xl leading-tight text-anthracite"
              >
                Vos travaux à Tours, Fondettes & partout en Indre-et-Loire.
              </h2>
              <p className="mt-6 text-anthracite/60 leading-relaxed">
                Basés à Fondettes (37230), nous connectons particuliers et
                professionnels avec des artisans partenaires implantés dans un rayon de
                40 km autour de Tours pour tous types de travaux : rénovation,
                climatisation, façade, toiture, isolation. Un devis sous 48h, où
                que vous soyez dans le 37.
              </p>
            </div>

            <div className="lg:col-span-8">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                {[
                  "Tours",
                  "Fondettes",
                  "Saint-Cyr-sur-Loire",
                  "La Riche",
                  "Joué-lès-Tours",
                  "Saint-Avertin",
                  "Chambray-lès-Tours",
                  "Saint-Pierre-des-Corps",
                  "Ballan-Miré",
                  "Luynes",
                  "Saint-Genouph",
                  "Berthenay",
                  "Savonnières",
                  "Vouvray",
                  "Rochecorbon",
                  "Montlouis-sur-Loire",
                  "Amboise",
                  "Langeais",
                ].map((city) => (
                  <div
                    key={city}
                    className="flex items-center gap-2 text-sm text-anthracite/80 py-1.5 border-b border-black/5"
                  >
                    <MapPin className="w-3 h-3 text-terracotta" strokeWidth={2} />
                    <span>Mise en relation & devis à {city}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-anthracite/50 leading-relaxed">
                Une commune non listée ? Nous intervenons également dans toute l'Indre-et-Loire (37) et sur la région Centre-Val de Loire pour les chantiers importants. N'hésitez pas à nous <Link to="/contact" className="underline hover:text-terracotta">contacter</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FAQ (SEO — FAQPage schema) ===================== */}
      <section className="container-editorial py-20 md:py-24" aria-labelledby="faq-title">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="eyebrow mb-4">Questions fréquentes</div>
            <h2 id="faq-title" className="font-display text-4xl md:text-5xl leading-tight text-anthracite">
              Vos questions,
              <br />
              <span className="text-terracotta">nos réponses.</span>
            </h2>
          </div>
          <div className="lg:col-span-8">
            <div className="divide-y divide-black/10 border-t border-b border-black/10">
              {[
                {
                  q: "Combien coûte l'installation d'une climatisation à Tours ou Fondettes ?",
                  a: "Le prix d'une climatisation dépend du type (split, multi-split, gainable, PAC air/eau), de la surface à traiter et de la configuration du logement. Pour un split mural mono-zone posé par un professionnel partenaire, comptez à partir de 1 500 € TTC. Pour une PAC air/eau avec pose, entre 8 000 € et 15 000 € avant aides. Les partenaires PENOW vous transmettent un devis détaillé et gratuit sous 48h.",
                },
                {
                  q: "Suis-je éligible aux aides pour ma pompe à chaleur en 2025 ?",
                  a: "Oui, la plupart des installations de PAC air/air ou air/eau ouvrent droit à MaPrimeRénov', aux CEE et à la TVA à 5,5%, à condition d'être posées par un professionnel certifié RGE QualiPAC. PENOW ne vous met en relation qu'avec des professionnels partenaires disposant de cette qualification. Selon vos revenus, la prime peut atteindre 5 000 €.",
                },
                {
                  q: "Combien de temps dure un chantier de rénovation ?",
                  a: "Cela dépend de l'ampleur : un ravalement de façade prend en général 1 à 3 semaines, un démoussage de toiture 1 à 2 jours, l'installation d'une PAC 1 à 2 jours, une rénovation complète d'appartement 4 à 12 semaines. Le planning détaillé vous est remis par le professionnel partenaire avant le début du chantier.",
                },
                {
                  q: "Qui réalise concrètement mes travaux ?",
                  a: "PENOW est un apporteur d'affaires : nous vous mettons en relation avec un professionnel partenaire sélectionné dans notre réseau. C'est ce professionnel qui vous adresse le devis, réalise les travaux, et vous délivre les garanties légales (parfait achèvement, biennale, décennale) sous sa propre responsabilité et ses propres assurances.",
                },
                {
                  q: "Vos partenaires sont-ils certifiés RGE ?",
                  a: "Oui — PENOW ne référence que des professionnels partenaires certifiés RGE (QualiPAC, Qualibois, Qualibat selon la spécialité). Cela vous permet de bénéficier des aides de l'État (MaPrimeRénov', CEE, TVA 5,5%) sur les prestations éligibles.",
                },
                {
                  q: "Intervenez-vous dans tout le département 37 ?",
                  a: "Oui, notre réseau couvre toute l'Indre-et-Loire (Tours, Fondettes, Saint-Cyr-sur-Loire, Joué-lès-Tours, Amboise, Chinon, Loches…) et s'étend à la région Centre-Val de Loire pour les chantiers importants.",
                },
              ].map((f, i) => (
                <details key={i} className="group py-5 cursor-pointer">
                  <summary className="flex items-start justify-between gap-6 font-display text-lg text-anthracite hover:text-terracotta transition-colors list-none">
                    <span>{f.q}</span>
                    <span className="text-terracotta font-sans text-2xl leading-none shrink-0 mt-1 group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-anthracite/70 leading-relaxed max-w-3xl">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== CTA FOOTER ===================== */}
      <section className="container-editorial pb-24" data-testid={HOME.ctaFooter}>
        <div className="bg-anthracite text-white p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="eyebrow mb-4 text-terracotta">Prêt à démarrer ?</div>
            <h2 className="font-display text-3xl md:text-5xl leading-tight">
              Parlons de votre projet.
              <br />
              <span className="text-white/60">Devis gratuit, sans engagement.</span>
            </h2>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-terracotta hover:bg-copper text-white px-6 py-4 tracking-wide transition-colors"
              data-testid="cta-footer-quote"
            >
              Demander un devis
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <a
              href={COMPANY.phoneHref}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white px-2 py-2"
              data-testid="cta-footer-phone"
            >
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
